import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { safetyMargins } from '@bedrock-rdk/constants';
import { SELECTED_SIGNAL } from '../signals';
import { Facet } from '../facet/facet.component';
import { CONTENT_GAP } from '../facet/facet.constants';
import {
  defaultContext,
  FACET_BORDER,
  getInitialStyle,
  HEIGHT,
  TOP_OFFSET_BY_RENDERING_CONTEXT,
} from './facetRow.style';

export class FacetRow extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Row: {},
      },
      getInitialStyle(),
    );
  }

  static getOffsetsByBlockData({ screenSize, renderingContext }) {
    const top = TOP_OFFSET_BY_RENDERING_CONTEXT[renderingContext];

    return {
      top: top && Number.isFinite(top[screenSize]) ? top[screenSize] : 0,
      bottom: 0,
      h: HEIGHT[screenSize],
    };
  }

  _construct() {
    this._style = defaultContext;
    this._focusedIndex = 0;
    this._contentPadding = 0;
    this._containerWidth = 0;
  }

  _detach() {
    this.tag('Row').onUpdate = null;
  }

  get data() {
    return this._data;
  }

  set data(value) {
    if (this._data === value) {
      return;
    }

    this._data = value;
    this._focusedIndex = 0;
    const { screenSize } = this._style;
    const row = this.tag('Row');

    if (!value || !value.items || !value.items.length) {
      row.onUpdate = undefined;
      row.children = [];

      return;
    }

    const { top } = FacetRow.getOffsetsByBlockData({ screenSize, renderingContext: value.renderingContext });
    row.y = top;
    row.children =
      value.items.map((item, index) => ({
        type: Facet,
        data: item,
        y: FACET_BORDER[screenSize],
        flexItem: {
          marginLeft: index ? CONTENT_GAP[screenSize] : 0,
        },
        passSignals: {
          [SELECTED_SIGNAL]: true,
        },
      })) || [];

    row.onUpdate = () => {
      this._containerWidth = _get(row, 'flex.item.w') || 0;
      this.goTo(this._focusedIndex, false);
    };
  }

  /**
   * Left padding: Sidenav width + padding
   *
   * @param {Number} value
   */
  set contentPadding(value) {
    this._contentPadding = value;
    this.goTo(this._focusedIndex, false);
  }

  /**
   * Scrolls the facet row so the item at `index` stays within the visible area,
   * left-aligned once the content overflows the row width.
   *
   * @param {Number} index
   * @param {Boolean} animate
   */
  goTo(index = this._focusedIndex, animate = true) {
    const row = this.tag('Row');
    if (!row.children.length) {
      return;
    }

    const flexItem = _get(row, `flex._layout.items.${index}`);
    if (!flexItem) {
      return;
    }

    const totalWidth = _get(row, 'flex.item.w') || this._containerWidth;
    const rightPadding = safetyMargins.horizontal[this._style.screenSize];
    const visibleWidth = this.finalW - this._contentPadding - rightPadding;
    const maxOffset = Math.min(visibleWidth, totalWidth) - totalWidth;
    const offset = -flexItem.x;
    const result = this._contentPadding + Math.max(Math.min(0, offset), maxOffset);

    if (animate) {
      row.setSmooth('x', result);
    } else {
      row.x = result;
    }

    this._focusedIndex = index;
  }

  _handleLeft() {
    if (this._focusedIndex > 0) {
      this.goTo(this._focusedIndex - 1);

      return true;
    }

    return false;
  }

  _handleRight() {
    if (this._focusedIndex < this.tag('Row').children.length - 1) {
      this.goTo(this._focusedIndex + 1);

      return true;
    }

    return false;
  }

  _captureKey() {
    this.tag('Row').onUpdate = null;

    return false;
  }

  _getFocused() {
    return this.tag('Row').children[this._focusedIndex];
  }
}
